import { useEffect, useState } from 'react'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'
import { redlistOptions } from '../../Page/ProjectPage/CreateProjectModal.tsx'
import { taxaOptions } from '../../Page/HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.ts'
import type { SpinAlgorithmKey } from '../../../Generated/SpinAlgorithm.ts'
import { SpinAlgorithms } from '../../../Generated/SpinAlgorithm.ts'
import type { TargetTaxonomyKey } from '../../../Generated/TargetTaxonomy.ts'
import type { ReportRequest } from '../../../Generated/ReportRequest.ts'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { canChooseSpinAlgorithm } from '../../../Model/index.js'
import { PREFERRED_SPIN_ALGORITHM } from '../../../Model/Spin/spinAlgorithm.js'

export interface ReportSettingsModalProps {
  title: string
  /**
   * An order's target taxonomy is chosen once, at order creation, and the pipeline has already run
   * against it — offering it again here invited a choice that was never really open. Projects span
   * orders and genuinely do pick one, so the selector is theirs. The redlist mode is hidden on orders
   * the same way.
   */
  scope: 'order' | 'project'
  /** Wording for the taxonomy dropdown's "leave it to the scope" entry. Project scope only. */
  scopeDefaultLabel?: string
  redlistDefaultLabel: string
  /** The scope's own settings, pre-selected when the popup opens. */
  defaultTaxonomy?: TargetTaxonomyKey | null
  defaultRedlist?: RedlistModeKey | null
  /** May use SPIN on this scope (INSIGHT, resolved by the caller, which knows the order or project). */
  spinAccess: boolean
  testId: string
  onConfirm: (settings: ReportRequest) => void
}

/** This modal renders its own body, so callers supply settings and handlers but never `children`. */
type Props = ReportSettingsModalProps & Omit<BaseModalProps, 'children'>

/**
 * Settings popup shown every time a user asks to generate a report, for either scope.
 *
 * Grouping (cross-order OTU merging) is deliberately *not* offered: it is an internal, not-yet-implemented
 * project setting, and reports default to no grouping at all.
 */
export const ReportSettingsModal = ({
  isOpen,
  onClose,
  title,
  scope,
  scopeDefaultLabel,
  redlistDefaultLabel,
  defaultTaxonomy,
  defaultRedlist,
  spinAccess,
  testId,
  onConfirm
}: Props) => {
  const user = useGetAuthenticatedUser()
  const algorithmChoice = canChooseSpinAlgorithm(user)

  const [reportTaxonomy, setReportTaxonomy] = useState<TargetTaxonomyKey | null>(null)
  const [redlistMode, setRedlistMode] = useState<RedlistModeKey | null>(null)
  const [spinMode, setSpinMode] = useState(false)
  const [spinAlgorithm, setSpinAlgorithm] = useState<SpinAlgorithmKey>(PREFERRED_SPIN_ALGORITHM)
  const [useLatest, setUseLatest] = useState(true)
  const [pointInTime, setPointInTime] = useState('')

  useEffect(() => {
    if (isOpen) {
      setReportTaxonomy(defaultTaxonomy ?? null)
      setRedlistMode(defaultRedlist ?? null)
      setSpinMode(false)
      setSpinAlgorithm(PREFERRED_SPIN_ALGORITHM)
      setUseLatest(true)
      setPointInTime('')
    }
  }, [isOpen, defaultTaxonomy, defaultRedlist])

  const handleSubmit = () => {
    /* The toggle is hidden without access, so a leftover spinMode must not reach the request. */
    const spinRequested = spinMode && spinAccess

    /*
     * "Latest" is sent as *absent*, never as a frozen `new Date()`. Stamping a clock here would make every click a
     * distinct report — the point in time is part of a report's identity — so an already-generated report could
     * never be served back. The backend instead regenerates a latest-mode report only once the scope's SPIN
     * membership has actually moved.
     */
    const resolvedPointInTime = spinRequested && !useLatest && pointInTime ? new Date(pointInTime).toISOString() : undefined

    onConfirm({
      reportTaxonomy: reportTaxonomy ?? undefined,
      redlistMode: redlistMode ?? undefined,
      spinMode: spinRequested,
      spinAlgorithm: spinRequested ? (algorithmChoice ? spinAlgorithm : PREFERRED_SPIN_ALGORITHM) : undefined,
      spinPointInTime: resolvedPointInTime
    })
    onClose()
  }

  return (
    <BaseModal onClose={onClose} isOpen={isOpen} testId={testId}>
      <div className="modal-box">
        <h3 className="text-lg font-bold">{title}</h3>

        {/* Hidden, not removed, for an order: the request still carries `defaultTaxonomy` and `defaultRedlist`. */}
        {scope === 'project' && (
          <div className="form-control w-full mt-4">
            <label className="label">
              <span className="label-text">Report Taxonomy</span>
            </label>
            <select
              data-testid="report-taxonomy"
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={reportTaxonomy || ''}
              onChange={(e) => setReportTaxonomy((e.target.value as TargetTaxonomyKey) || null)}
            >
              <option value="">{scopeDefaultLabel}</option>
              {taxaOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {scope === 'project' && (
          <div className="form-control w-full mt-4">
            <label className="label">
              <span className="label-text">Redlist Mode</span>
            </label>
            <select
              data-testid="report-redlist"
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={redlistMode || ''}
              onChange={(e) => setRedlistMode((e.target.value as RedlistModeKey) || null)}
            >
              <option value="">{redlistDefaultLabel}</option>
              {redlistOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {spinAccess && (
          <div className="form-control w-full mt-6">
            <label className="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                data-testid="report-spin-toggle"
                className="toggle toggle-primary"
                checked={spinMode}
                onChange={(e) => setSpinMode(e.target.checked)}
              />
              <span className="label-text">
                SPIN report
                <br />
                <span className="text-xs">One line per SPIN (canonical cluster) instead of one line per OTU.</span>
              </span>
            </label>
          </div>
        )}

        {spinAccess && spinMode && (
          <div className="mt-2 pl-4 border-l-2 border-primary/40">
            {algorithmChoice && (
              <div className="form-control w-full mt-2">
                <label className="label">
                  <span className="label-text">SPIN Algorithm</span>
                </label>
                <select
                  data-testid="report-spin-algorithm"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={spinAlgorithm}
                  onChange={(e) => setSpinAlgorithm(e.target.value as SpinAlgorithmKey)}
                >
                  {Object.values(SpinAlgorithms).map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-control w-full mt-4">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  data-testid="report-spin-latest"
                  className="checkbox checkbox-primary"
                  checked={useLatest}
                  onChange={(e) => setUseLatest(e.target.checked)}
                />
                <span className="label-text">Use latest assignments (now)</span>
              </label>
              {!useLatest && (
                <input
                  type="datetime-local"
                  data-testid="report-spin-point-in-time"
                  className="mt-2 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={pointInTime}
                  onChange={(e) => setPointInTime(e.target.value)}
                />
              )}
              <span className="text-xs mt-1">Per OTU, the latest SPIN assignment at or before this point in time is used.</span>
            </div>
          </div>
        )}

        <div className="modal-action">
          <button className="btn btn-ghost mr-2" onClick={() => onClose()}>
            Cancel
          </button>
          <button data-testid="generate-report" className="btn btn-primary" onClick={handleSubmit}>
            Generate Report
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
